import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { ArrowLeft, Check } from 'lucide-react';
import { serviceDetails } from '../mockData';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const service = serviceDetails.find(s => s.id === serviceId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            Go Back Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[500px] overflow-hidden">
        <img 
          src={service.image} 
          alt={service.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-indigo-900/70"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-6">
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="mb-6 text-white border-white hover:bg-white hover:text-blue-600 transition-all duration-300"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
            <span className="inline-block bg-indigo-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {service.category}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">{service.title}</h1>
            <p className="text-2xl text-white/90 max-w-3xl">{service.shortDescription}</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            {/* Overview */}
            <div className="mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Overview</h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">{service.description}</p>
              <p className="text-lg text-gray-600 leading-relaxed">{service.detailedDescription}</p>
            </div>

            {/* Key Features */}
            <div className="mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-8">Key Features</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {service.features.map((feature, index) => (
                  <Card key={index} className="border-2 border-blue-100 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Check className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                          <p className="text-gray-600">{feature.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-8">Benefits</h2>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                <ul className="space-y-4">
                  {service.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Check className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                      <span className="text-lg text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Technologies */}
            <div className="mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-8">Technologies We Use</h2>
              <div className="flex flex-wrap gap-3">
                {service.technologies.map((tech, index) => (
                  <span 
                    key={index}
                    className="px-6 py-3 bg-white border-2 border-blue-200 rounded-full text-gray-700 font-medium hover:bg-blue-50 hover:border-blue-400 transition-all duration-300"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* Pricing Section */}
            {service.pricing && service.pricing.length > 0 && (
              <div className="mb-16">
                <div className="text-center mb-12">
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">Pricing Plans</h2>
                  <p className="text-xl text-gray-600">Choose the perfect plan for your needs</p>
                </div>
                <div className="grid md:grid-cols-3 gap-8">
                  {service.pricing.map((plan, index) => (
                    <div 
                      key={index}
                      className={`bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 ${
                        index === 1 ? 'border-4 border-blue-500 relative' : 'border-2 border-gray-200'
                      }`}
                    >
                      {index === 1 && (
                        <div className="absolute top-0 right-6 transform -translate-y-1/2 z-10">
                          <span className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-2 rounded-full text-sm font-bold shadow-lg whitespace-nowrap">
                            POPULAR
                          </span>
                        </div>
                      )}
                      <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                        <div className="mb-6">
                          <span className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                            {plan.price}
                          </span>
                        </div>
                      </div>
                      <ScrollArea className="h-80 mb-8 pr-4">
                        <ul className="space-y-4">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start">
                              <Check className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </ScrollArea>
                      
                      {/* Choose Plan Button */}
                      <Button 
                        onClick={() => navigate(`/checkout?plan=${encodeURIComponent(plan.name)}&amount=${plan.price.replace(/[^0-9.]/g, '')}&service=${encodeURIComponent(service.name)}`)}
                        className={`w-full py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 mb-3 ${
                          index === 1
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
                            : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        Choose Plan
                      </Button>
                      
                      {/* Or Contact Button */}
                      <Button 
                        onClick={() => navigate('/contact')}
                        className="w-full py-4 text-base rounded-full shadow-md hover:shadow-lg transition-all duration-300 bg-white text-gray-600 border-2 border-gray-300 hover:bg-gray-50"
                      >
                        Contact Us
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-12 text-center text-white">
              <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="text-xl mb-8 text-white/90">Let's discuss how we can help transform your business with our {service.title} services.</p>
              <Button 
                onClick={() => navigate('/contact')}
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                Contact Us Today
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;